import type { ConnectAskResponse, ConnectCitation } from "@ando/core";
import { Composer, type MessageBodyCitationRef, Message as MessageView, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Info, Sparkles } from "lucide-react";
import * as React from "react";

/**
 * ConnectChat — the ONE reusable, transport-agnostic Q&A surface for Ando
 * Connect Spaces (June 2026).
 *
 * Design goal: a SINGLE chat component drives BOTH the authed member view
 * (``askInSpace`` over the session cookie / guest header) AND the public
 * guest / chatbot view (``askChatbot`` over the unauthenticated public
 * endpoint). The transport is the ONLY difference, so it is INJECTED via the
 * ``ask`` prop — this component knows nothing about the global conversation
 * store, websockets, or which auth tier it runs under.
 *
 *   member view  → ask = (q) => askInSpace(spaceId, { question: q }, cred?)
 *   public view  → ask = (q) => askChatbot(publicToken, { question: q })
 *
 * It owns its OWN local message state (no Zustand, no ``useChatStore``) so it
 * can be mounted on the public marketing surface (``apps/web``) or inside an
 * iframe embed without dragging the workspace shell along. The look is
 * mirrored from the main chat surface by reusing the SAME @ando/ui primitives
 * the main ``Messages`` component uses: ``Message`` (bubble + markdown body +
 * citations) and ``Composer`` (cream card + auto-resize textarea + accent
 * send button). No fork of the conversation store, no duplicated markdown/
 * bubble/composer code — DRY.
 */

export interface ConnectChatProps {
  /**
   * Injected transport. Resolves a scoped ``ConnectAskResponse``. The two
   * callers pass different bindings (member ``askInSpace`` vs public
   * ``askChatbot``) — this component stays identical for both.
   */
  readonly ask: (question: string) => Promise<ConnectAskResponse>;
  /** Optional header title (e.g. the Space name). */
  readonly title?: React.ReactNode;
  /** Optional sub-line under the title. */
  readonly subtitle?: React.ReactNode;
  /**
   * Persistent scope indicator rendered above the thread (typically a
   * ``<SpaceScopeBanner>``). Kept as a slot so the public chatbot view can
   * pass a lighter banner / branding line.
   */
  readonly scopeBanner?: React.ReactNode;
  /**
   * Optional branding node rendered in the header right slot (white-label
   * logo on the public chatbot surface). Authed view leaves it empty.
   */
  readonly branding?: React.ReactNode;
  /** Hard-disable the composer (e.g. archived space, revoked access). */
  readonly disabled?: boolean;
  /** Empty-state hint shown before the first question. */
  readonly emptyHint?: React.ReactNode;
  /** Composer placeholder. */
  readonly placeholder?: string;
  /** Wrapper className. */
  readonly className?: string;
}

interface ChatTurn {
  readonly id: string;
  readonly role: "user" | "assistant";
  readonly content: string;
  readonly citations?: MessageBodyCitationRef[];
  /** Server flagged the answer as capped to the allowed-doc subset. */
  readonly capped?: boolean;
  readonly allowedDocCount?: number;
  readonly error?: boolean;
}

/**
 * Map the BE ``ConnectCitation`` (open shape, ``[key: string]: unknown``)
 * onto the @ando/ui ``MessageBodyCitationRef`` the shared ``MessageBody``
 * renderer consumes — same citation chip styling as the main chat. We read
 * common fields defensively because the wire type is intentionally loose.
 */
function mapCitations(
  citations: ConnectCitation[] | undefined,
): MessageBodyCitationRef[] | undefined {
  if (!citations || citations.length === 0) return undefined;
  return citations.map((c, i) => {
    const sourceId =
      typeof c.source_id === "string"
        ? c.source_id
        : typeof c.id === "string"
          ? c.id
          : `cite-${i + 1}`;
    const title =
      typeof c.title === "string"
        ? c.title
        : typeof c.citation_title === "string"
          ? c.citation_title
          : undefined;
    return {
      index: i + 1,
      sourceId,
      title,
      url: typeof c.url === "string" ? c.url : undefined,
      snippet: typeof c.snippet === "string" ? c.snippet : undefined,
    } satisfies MessageBodyCitationRef;
  });
}

let TURN_SEQ = 0;
function nextTurnId(): string {
  TURN_SEQ += 1;
  return `connect-turn-${TURN_SEQ}`;
}

export function ConnectChat({
  ask,
  title,
  subtitle,
  scopeBanner,
  branding,
  disabled = false,
  emptyHint,
  placeholder,
  className,
}: ConnectChatProps) {
  const { i18n } = useLingui();
  const [turns, setTurns] = React.useState<readonly ChatTurn[]>([]);
  const [draft, setDraft] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  // Guard against state updates landing after unmount (iframe teardown).
  const aliveRef = React.useRef(true);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Auto-scroll the thread to the newest turn / pending indicator — but ONLY
  // when the user is already near the bottom, so we never yank someone who
  // scrolled UP to re-read an earlier answer back down. Mirrors the main chat's
  // guarded ``scrollToBottom``; ``scrollIntoView`` honours OS reduced-motion.
  const scrollToBottom = React.useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);
  const NEAR_BOTTOM_PX = 80;
  const turnCount = turns.length;
  React.useEffect(() => {
    if (turnCount === 0 && !pending) return;
    const el = scrollRef.current;
    const nearBottom =
      !el || el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    if (nearBottom) scrollToBottom();
  }, [turnCount, pending, scrollToBottom]);

  const canSend = draft.trim().length > 0 && !pending && !disabled;

  const submit = React.useCallback(async () => {
    const question = draft.trim();
    if (!question || pending || disabled) return;
    setDraft("");
    setPending(true);
    setTurns((prev) => [...prev, { id: nextTurnId(), role: "user", content: question }]);
    try {
      const res = await ask(question);
      if (!aliveRef.current) return;
      setTurns((prev) => [
        ...prev,
        {
          id: nextTurnId(),
          role: "assistant",
          content: res.answer_markdown?.trim() || i18n._(msg`Nessuna risposta disponibile.`),
          citations: mapCitations(res.citations),
          capped: res.capped,
          allowedDocCount: res.allowed_doc_count,
        },
      ]);
    } catch (err) {
      if (!aliveRef.current) return;
      const message =
        err instanceof Error ? err.message : i18n._(msg`Si è verificato un errore. Riprova.`);
      setTurns((prev) => [
        ...prev,
        {
          id: nextTurnId(),
          role: "assistant",
          content: message,
          error: true,
        },
      ]);
    } finally {
      if (aliveRef.current) setPending(false);
    }
  }, [ask, draft, pending, disabled, i18n]);

  const isEmpty = turns.length === 0 && !pending;

  // Dedicated screen-reader announcement of the latest answer. A polite live
  // region on the whole scrolling thread often fails to announce complex
  // appended nodes (markdown + citations), so we mirror the newest assistant
  // answer's PLAIN text into a small visually-hidden live region instead. It
  // only carries text once a turn has settled (``!pending``), so the typing
  // indicator's own role=status owns the "thinking" announcement.
  const lastAnswer = React.useMemo(() => {
    if (pending) return "";
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      const t = turns[i];
      if (t && t.role === "assistant") return t.content;
    }
    return "";
  }, [turns, pending]);

  return (
    <section
      className={cn(
        "flex h-full min-h-0 flex-col rounded-[14px] border border-hairline/70 bg-white/45",
        className,
      )}
      aria-label={i18n._(msg`Chat dello spazio`)}
    >
      {/* Header — title + subtitle on the left, white-label branding on the
          right. Hidden entirely when the caller supplies neither so the
          embed surface can stay chrome-free. */}
      {title || subtitle || branding ? (
        <header className="flex items-start justify-between gap-3 border-hairline/60 border-b px-4 py-3">
          <div className="min-w-0">
            {title ? (
              <h2 className="truncate font-semibold text-[14px] text-ink-1">{title}</h2>
            ) : null}
            {subtitle ? <p className="mt-0.5 truncate text-[12px] text-ink-2">{subtitle}</p> : null}
          </div>
          {branding ? <div className="shrink-0">{branding}</div> : null}
        </header>
      ) : null}

      {scopeBanner ? <div className="px-4 pt-3">{scopeBanner}</div> : null}

      {/* Visually-hidden live region — announces the newest settled answer's
          plain text to screen readers. The scrolling thread below is NOT a
          live region (complex appended markdown/citation nodes announce
          unreliably); ``aria-busy`` there still flags the in-flight state. */}
      <p className="sr-only" aria-live="polite">
        {lastAnswer}
      </p>

      {/* Thread — ``aria-busy`` flags the pending fetch; announcements are
          delegated to the dedicated live region above. */}
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-5"
        aria-busy={pending}
      >
        {isEmpty ? (
          <div className="m-auto flex max-w-[28rem] flex-col items-center gap-2 px-4 text-center">
            <span
              aria-hidden
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-ando-orange-soft text-ando-orange"
            >
              <Sparkles size={18} strokeWidth={1.75} />
            </span>
            <p className="text-[13px] text-ink-2">
              {emptyHint ?? (
                <Trans>
                  Fai una domanda. Ando risponde solo dalle fonti condivise in questo spazio.
                </Trans>
              )}
            </p>
          </div>
        ) : (
          <>
            {turns.map((t) => (
              <React.Fragment key={t.id}>
                <MessageView
                  kind={t.role === "user" ? "user" : "agent"}
                  markdown={t.content}
                  author={t.role === "user" ? undefined : "Ando"}
                  status={t.error ? "error" : undefined}
                  citations={t.role === "assistant" ? t.citations : undefined}
                />
                {/* Capped-scope notice — friendly, never alarming. The BE
                    sets ``capped`` when the answer was limited to the
                    allowed-doc subset (guest scope narrower than the
                    question). Semantic color + ICON, not color alone. */}
                {t.role === "assistant" && t.capped ? (
                  <div
                    role="note"
                    className="ml-9 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-[12px] text-ink-2"
                  >
                    <Info
                      size={14}
                      strokeWidth={1.9}
                      aria-hidden
                      className="mt-px shrink-0 text-warning-foreground"
                    />
                    <span>
                      {t.allowedDocCount && t.allowedDocCount > 0 ? (
                        <Trans>
                          Risposta limitata alle{" "}
                          <strong className="tabular-nums">{t.allowedDocCount}</strong> fonti
                          condivise con te in questo spazio.
                        </Trans>
                      ) : (
                        <Trans>
                          Risposta limitata alle fonti condivise con te in questo spazio.
                        </Trans>
                      )}
                    </span>
                  </div>
                ) : null}
              </React.Fragment>
            ))}
            {pending ? <MessageView kind="typing" author="Ando" /> : null}
          </>
        )}
        <div ref={bottomRef} aria-hidden />
      </div>

      {/* Composer — reuses the SAME @ando/ui Composer primitives as the main
          chat (cream card, auto-resize textarea, accent send). */}
      <div className="px-4 pb-4">
        <Composer.Root
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Composer.Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onSubmit={() => void submit()}
            disabled={disabled || pending}
            placeholder={placeholder ?? i18n._(msg`Scrivi una domanda…`)}
            aria-label={i18n._(msg`Domanda allo spazio`)}
          />
          <Composer.Bar
            right={<Composer.SendButton canSend={canSend} onSend={() => void submit()} />}
          />
        </Composer.Root>
      </div>
    </section>
  );
}
ConnectChat.displayName = "ConnectChat";
